<script setup lang="ts">
import axios from 'axios'
import { notifyError, fullLogOTT, getDevicesFromTree, msToTime } from '@/utils/helpers'
import type { OmniTBObjectTree } from '@/types/omniTBCustomer'
import type { DataTableHeaders } from '@/plugins/vuetify'
import type { ECOption } from '@/plugins/echarts'



const apiOmniAnon = axios.create({
  baseURL: import.meta.env.VITE_OMNI_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

let apiConfig: { headers: Record<string, string> } | null = null
const isLoading = ref(true)
const isLoadingSelected = ref(false)

declare interface Telemetry {
  ts : number
  year: number
  month: number
  day: number
  data_Cumul: number
  realAmount: number
}

declare interface TableTelemetry {
  id: string
  name: string
  data_Cumul: number
  data_CurrentFlow: number
  last_ts: number
  monthlyData: Telemetry[]
}

const wmeters = ref<OmniTBObjectTree[]>([])
const telemetries = ref<TableTelemetry[]>([])

const selectedTelemetry = ref<TableTelemetry | null>(null)
const wmeter = ref<OmniTBObjectTree | null>(null)
const currentFlow = ref(0)
const dailyData = ref<Telemetry[]>([])

const headers = ref<DataTableHeaders>([
  { title: 'Nom', key: 'name' },
  { title: 'Cumulatif', key: 'data_Cumul' },
  { title: 'Flot actuel', key: 'data_CurrentFlow' },
  { title: 'Dernière donnée', key: 'last_ts' }
])

onMounted(async () => {
  isLoading.value = true

  const log = await fullLogOTT(apiOmniAnon, window, null)
  if (log == null || log.error != null || log.customer == null || log.customer.objectTree == null) {
    notifyError(log?.error ?? 'Unknown error during OTT login')
    isLoading.value = false
    return
  }

  apiConfig = { headers: { 'X-APIKey': log.apiKey, 'X-ThingsBoard': log.tbToken } }

  wmeters.value = getDevicesFromTree(log.customer.objectTree, 'WMETER')

  try {
    for (const wmeter of wmeters.value) {
      const tele = await apiOmniAnon.get('/api/tb/telemetry/DEVICE/' + wmeter.id.id + '/values/latestTimeseries', apiConfig)
      if (tele.data) {
        const tel: TableTelemetry = {
          id: wmeter.id.id,
          name: wmeter.name,
          data_Cumul: tele.data['data_Cumul'][0]?.value || 0,
          data_CurrentFlow: tele.data['data_CurrentFlow'][0]?.value || 0,
          last_ts: tele.data['data_Cumul'][0]?.ts || 0,
          monthlyData: []
        }

        telemetries.value.push(tel)
        //console.log(tel)
      }
    }

    for (const wmeter of wmeters.value) {
      const monthlyData: Telemetry[] = []

        // Get the timeseries of the last 13 months
        for (let i = 13; i >= 0; i--) {
          const startTs = new Date()
          startTs.setMonth(startTs.getMonth() - i)
          startTs.setDate(1)
          startTs.setHours(12, 0, 0, 0)
          const endTs = new Date(startTs)
          endTs.setMonth(endTs.getMonth() + 1)

          const teleHistory = await apiOmniAnon.get('/api/tb/telemetry/DEVICE/' + wmeter.id.id + '/values/timeseries' +
            '?keys=data_Cumul&startTs=' + startTs.getTime() + '&endTs=' + endTs.getTime() +
            '&limit=1', apiConfig)

          monthlyData.push({
            ts: teleHistory.data['data_Cumul']?.[0]?.ts || 0,
            year: startTs.getFullYear(),
            month: startTs.getMonth() + 1,
            day: startTs.getDate(),
            data_Cumul: teleHistory.data['data_Cumul']?.[0]?.value || 0,
            realAmount: 0
          })
        }

        for (let i = 0; i < monthlyData.length - 1; i++) {
          monthlyData[i].realAmount = monthlyData[i + 1].data_Cumul - monthlyData[i].data_Cumul
        }

        const tele = telemetries.value.find(t => t.id === wmeter.id.id)
        if (tele) {
          monthlyData[monthlyData.length - 1].realAmount = tele.data_Cumul - monthlyData[monthlyData.length - 1].data_Cumul
          tele.monthlyData = monthlyData
        }
    }

    //console.log('Telemetry:', telemetries.value)
  } catch (error) {
    notifyError('Error fetching telemetry: ' + error)
  }

  isLoading.value = false
})

async function selectWmeter(_event: Event, row: { item: TableTelemetry }) {
  const item = row.item
  const match = wmeters.value.find(w => w.id.id === item.id)
  if (!match) return
  wmeter.value = match
  currentFlow.value = item.data_CurrentFlow
  selectedTelemetry.value = item

  dailyData.value = []
  if (!apiConfig) return

  isLoadingSelected.value = true

  try {
    // Fetch daily data for the last 60 days
    const daily: Telemetry[] = []
    for (let i = 60; i >= 0; i--) {
      const startTs = new Date()
      startTs.setDate(startTs.getDate() - i)
      startTs.setHours(0, 0, 0, 0)
      const endTs = new Date(startTs)
      endTs.setDate(endTs.getDate() + 1)

      const teleHistory = await apiOmniAnon.get('/api/tb/telemetry/DEVICE/' + item.id + '/values/timeseries' +
        '?keys=data_Cumul&startTs=' + startTs.getTime() + '&endTs=' + endTs.getTime() +
        '&limit=1', apiConfig)

      if (teleHistory && teleHistory.data && teleHistory.data['data_Cumul']) {
        daily.push({
          ts: teleHistory.data['data_Cumul']?.[0]?.ts || 0,
          year: startTs.getFullYear(),
          month: startTs.getMonth() + 1,
          day: startTs.getDate(),
          data_Cumul: teleHistory.data['data_Cumul']?.[0]?.value || 0,
          realAmount: 0
        })
      }
    }

    for (let i = 0; i < daily.length - 1; i++) {
      daily[i].realAmount = daily[i + 1].data_Cumul - daily[i].data_Cumul
    }
    if (daily.length > 0) {
      daily[daily.length - 1].realAmount = item.data_Cumul - daily[daily.length - 1].data_Cumul
    }
    dailyData.value = daily
  } catch (error) {
    notifyError('Erreur: ' + error)
  }

  isLoadingSelected.value = false
}

const dailyChartOption = computed<ECOption>(() => {
  if (dailyData.value.length === 0) return {}

  const categories = dailyData.value.map(
    m => `${m.day} ${monthNames[m.month]}`
  )

  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { top: 30, left: '2%', right: '2%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: categories,
      axisTick: { alignWithLabel: true },
      axisLabel: { rotate: 45 }
    },
    yAxis: {
      type: 'value',
      name: 'm³'
    },
    series: [{
      name: selectedTelemetry.value?.name ?? '',
      type: 'bar',
      data: dailyData.value.map(m => parseFloat(m.realAmount.toFixed(2)))
    }]
  }
})

const selectedChartOption = computed<ECOption>(() => {
  if (!selectedTelemetry.value) return {}

  const tel = selectedTelemetry.value
  const categories = tel.monthlyData.map(
    m => `${monthNames[m.month]} ${m.year}`
  )

  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { top: 0 },
    grid: { top: 30, left: '2%', right: '2%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: categories,
      axisTick: { alignWithLabel: true },
      axisLabel: { rotate: 45 }
    },
    yAxis: {
      type: 'value',
      name: 'm³'
    },
    series: [{
      name: tel.name,
      type: 'bar',
      data: tel.monthlyData.map(m => parseFloat(m.realAmount.toFixed(2)))
    }]
  }
})

const monthNames = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

function exportCsv() {
  if (telemetries.value.length === 0) return

  const months = telemetries.value[0]?.monthlyData.map(
    m => `${monthNames[m.month]}-${m.year}`
  ) || []

  const headerRow = ['Nom', 'Cumulatif (m³)', 'Flot actuel (m³/h)', ...months]
  const rows = telemetries.value.map(tel => [
    tel.name,
    tel.data_Cumul.toFixed(2),
    tel.data_CurrentFlow.toFixed(2),
    ...tel.monthlyData.map(m => m.realAmount.toFixed(2))
  ])

  const csv = '\uFEFF' + [headerRow, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'compteurs_eau.csv'
  a.click()
  URL.revokeObjectURL(url)
}

function exportDailyCsv() {
  if (dailyData.value.length === 0 || !selectedTelemetry.value) return

  const headerRow = ['Date', 'Consommation (m\u00b3)']
  const rows = dailyData.value.map(d => [
    `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`,
    d.realAmount.toFixed(2)
  ])

  const csv = '\uFEFF' + [headerRow, ...rows].map(r => r.join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${selectedTelemetry.value.name}_journalier.csv`
  a.click()
  URL.revokeObjectURL(url)
}

const chartOption = computed<ECOption>(() => {
  if (telemetries.value.length === 0) return {}

  // Build x-axis categories from the first wmeter's monthlyData
  const categories = telemetries.value[0]?.monthlyData.map(
    m => `${monthNames[m.month]} ${m.year}`
  ) || []

  const series = telemetries.value.map(tel => ({
    name: tel.name,
    type: 'bar' as const,
    data: tel.monthlyData.map(m => parseFloat(m.realAmount.toFixed(2)))
  }))

  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { top: 0 },
    grid: { top: 30, left: '2%', right: '2%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: categories,
      axisTick: { alignWithLabel: true },
      axisLabel: { rotate: 45 }
    },
    yAxis: {
      type: 'value',
      name: 'm³'
    },
    series
  }
})
</script>

<template>
  <v-row class="ma-1">
    <v-col cols="5">
      <v-data-table
        :headers="headers" :items="telemetries"
        :sort-by="[{ key: 'name', order: 'asc' }]" class="elevation-1" :loading="isLoading"
        @click:row="selectWmeter">
        <template #item.data_Cumul="{ item }">
          {{ item.data_Cumul.toFixed(2) }} m³
        </template>
        <template #item.data_CurrentFlow="{ item }">
          {{ item.data_CurrentFlow.toFixed(2) }} m³/h
        </template>
        <template #item.last_ts="{ item }">
          {{ msToTime(new Date().getTime() - item.last_ts) }}
        </template>
      </v-data-table>
    </v-col>
    <v-col cols="7">
      <v-btn class="mb-2" size="small" prepend-icon="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" :disabled="telemetries.length === 0" @click="exportCsv">Exporter CSV</v-btn>
      <v-chart style="min-height: 400px;" :option="chartOption" autoresize />
    </v-col>
  </v-row>
  <v-card v-if="wmeter" :loading="isLoadingSelected" class="ma-5">
    <v-row class="ma-1">
      <v-col cols="6">
        Nom: {{ wmeter.name }} <br>
        Cumulatif: {{ selectedTelemetry?.data_Cumul.toFixed(2) }} m³ <br>
        Flot actuel: {{ currentFlow }} m³/h <br>
        Dernière donnée: {{ msToTime(new Date().getTime() - selectedTelemetry!.last_ts) }}
      </v-col>
      <v-col cols="6">
        <v-chart style="min-height: 400px;" :option="selectedChartOption" autoresize />
      </v-col>
    </v-row>
    <v-row v-if="dailyData.length > 0" class="ma-1">
      <v-col cols="12">
        <v-btn class="mb-2" size="small" prepend-icon="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z" @click="exportDailyCsv">Exporter CSV</v-btn>
        <v-chart style="min-height: 400px;" :option="dailyChartOption" autoresize />
      </v-col>
    </v-row>
  </v-card>

  <v-footer app>
    <v-spacer/>
    <v-defaults-provider :defaults="{ VBtn: { variant: 'text', size: 'x-small' } }">
      <AppNotification />
      <ButtonSettings />
    </v-defaults-provider>
  </v-footer>
</template>
